import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AuthGate } from "@/components/AuthGate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { listReviews } from "@/lib/reviews.functions";
import {
  getMyProfile,
  setMyInstitution,
  requestInstitutionChange,
  listMyInstitutionChangeRequests,
} from "@/lib/institutions.functions";
import { setMyPreferredName } from "@/lib/admin.users.functions";
import { InstitutionPicker, type InstitutionValue } from "@/components/InstitutionPicker";
import { toast } from "sonner";
import { ArrowRight, Check, LayoutDashboard, FileText, Lock, Sparkles } from "lucide-react";
import { PageContainer } from "@/components/PageContainer";
import { DirectoryListingCard } from "@/components/DirectoryListingCard";
import { FreeTermCard } from "@/components/FreeTermCard";
import { Eyebrow, DisplayHeading } from "@/components/editorial";

export const Route = createFileRoute("/profile")({
  head: () => ({
    meta: [
      { title: "Learning profile — Tiger's Eye" },
      { name: "description", content: "Recurring patterns across your Tiger's Eye reviews to help focus your next draft." },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: () => (<AuthGate><Profile /></AuthGate>),
});

function Profile() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const fetchFn = useServerFn(listReviews);
  const profileFn = useServerFn(getMyProfile);
  const setInstFn = useServerFn(setMyInstitution);
  const reqChangeFn = useServerFn(requestInstitutionChange);
  const listChangeReqsFn = useServerFn(listMyInstitutionChangeRequests);

  const { data } = useQuery({ queryKey: ["reviews"], queryFn: () => fetchFn() });
  const { data: profData } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn() });
  const { data: changeReqs } = useQuery({
    queryKey: ["my-institution-change-requests"],
    queryFn: () => listChangeReqsFn(),
  });

  const total = data?.reviews.length ?? 0;
  const completed = data?.reviews.filter((r) => r.status === "complete").length ?? 0;

  const profile = profData?.profile;
  const currentValue: InstitutionValue = profile?.institution_id
    ? { kind: "registry", id: profile.institution_id, name: profile.institution_name ?? "" }
    : profile?.institution_name
    ? { kind: "free", name: profile.institution_name }
    : null;

  const emailDomain = user?.email?.split("@")[1] ?? null;

  // 7-day self-serve grace window
  const accountAgeMs = profile?.created_at
    ? Date.now() - new Date(profile.created_at).getTime()
    : 0;
  const withinGrace = accountAgeMs < 7 * 24 * 60 * 60 * 1000;
  const institutionLocked = !!(profile?.institution_id || profile?.institution_name);

  const updateInst = useMutation({
    mutationFn: async (next: InstitutionValue) => {
      if (!next) return setInstFn({ data: { institutionId: null, freeText: null } });
      if (next.kind === "registry") return setInstFn({ data: { institutionId: next.id } });
      return setInstFn({ data: { freeText: next.name } });
    },
    onSuccess: () => {
      toast.success("Institution updated");
      qc.invalidateQueries({ queryKey: ["my-profile"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Change-request form state
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [requestedInst, setRequestedInst] = useState<InstitutionValue>(null);
  const [changeReason, setChangeReason] = useState("");

  const submitChangeReq = useMutation({
    mutationFn: () =>
      reqChangeFn({
        data: {
          requestedInstitutionId:
            requestedInst?.kind === "registry" ? requestedInst.id : undefined,
          requestedFreeText:
            requestedInst?.kind === "free" ? requestedInst.name : undefined,
          reason: changeReason,
        },
      }),
    onSuccess: () => {
      toast.success("Change request submitted — support will be in touch");
      setShowChangeForm(false);
      setRequestedInst(null);
      setChangeReason("");
      qc.invalidateQueries({ queryKey: ["my-institution-change-requests"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const prefNameFn = useServerFn(setMyPreferredName);
  const [preferredName, setPreferredName] = useState<string>("");
  useEffect(() => {
    setPreferredName(profile?.preferred_name ?? "");
  }, [profile?.preferred_name]);

  const updatePrefName = useMutation({
    mutationFn: () => prefNameFn({ data: { preferred_name: preferredName.trim() || null } }),
    onSuccess: () => {
      toast.success("Preferred name saved");
      qc.invalidateQueries({ queryKey: ["my-profile"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const hasName = !!(profile?.preferred_name && profile.preferred_name.trim());
  const hasInstitution = !!(profile?.institution_id || profile?.institution_name);
  const nameDirty = preferredName.trim() !== (profile?.preferred_name ?? "");
  const setupComplete = hasName && hasInstitution && !nameDirty;
  const pendingChangeReq = (changeReqs?.requests ?? []).find((r) => r.status === "pending");

  const steps = [
    { label: "Create account", done: true },
    { label: "Your name", done: hasName && !nameDirty },
    { label: "Institution", done: hasInstitution },
    { label: "Start first review", done: total > 0 },
  ];

  const handleSaveAndContinue = async () => {
    try {
      if (nameDirty) await updatePrefName.mutateAsync();
      navigate({ to: "/reviews/new" });
    } catch {
      /* toast already shown */
    }
  };

  const isOnboarding = !setupComplete && total === 0;

  return (
    <PageContainer>
      {isOnboarding && (
        <div className="mb-8 border border-primary/40 bg-primary/5 p-5 rounded-md flex items-start gap-4 animate-fade-in">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <Eyebrow className="mb-1">Welcome to Tiger's Eye</Eyebrow>
            <p className="text-sm text-ink-secondary">
              Two quick steps and you'll be ready for your first review. We use your name to make feedback feel
              personal, and your institution to tailor referencing and level expectations. Nothing is shared.
            </p>
          </div>
        </div>
      )}

      <Eyebrow className="mb-3">Personal Profile</Eyebrow>


      {/* Onboarding stepper */}
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-10 text-xs font-mono tracking-wider">
        {steps.map((s, i) => (
          <li key={s.label} className="flex items-center gap-2">
            <span
              className={`flex items-center justify-center h-6 w-6 rounded-full border ${
                s.done
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-rule text-ink-tertiary"
              }`}
            >
              {s.done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={s.done ? "text-primary" : "text-ink-tertiary"}>
              {s.label.toUpperCase()}
            </span>
            {i < steps.length - 1 && <span className="text-ink-tertiary mx-1">›</span>}
          </li>
        ))}
      </ol>

      <section className="mb-10">
        <Eyebrow className="mb-3">1 · Your name</Eyebrow>
        {hasName ? (
          <div className="border border-rule bg-card px-3 py-2">
            <span className="text-sm text-primary">{profile?.preferred_name}</span>
          </div>
        ) : (
          <>
            <p className="text-sm text-ink-secondary mb-3">
              What should we call you? A nickname or first name helps our support team if you ever need a hand.
              Choose carefully — this is saved when you continue, and can&apos;t be changed yourself later.
            </p>
            <input
              type="text"
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
              maxLength={120}
              placeholder="e.g. Alex"
              className="w-full border border-border bg-background px-3 py-2 text-sm"
            />
          </>
        )}
      </section>


      <section className="mb-10">
        <Eyebrow className="mb-3">2 · Main institution</Eyebrow>
        <p className="text-sm text-ink-secondary mb-3">
          Your home institution for your current course. This is locked once set —
          request a change if you transfer, graduate, or start a new programme.
          {withinGrace && hasInstitution && (
            <span className="block text-xs text-ink-tertiary mt-1 font-mono tracking-wider">
              CORRECTABLE FOR {Math.max(0, 7 - Math.floor(accountAgeMs / 86_400_000))} MORE DAY(S)
            </span>
          )}
        </p>

        {institutionLocked ? (
          <>
            <div className="flex items-center justify-between border border-rule bg-card px-4 py-3">
              <div>
                <p className="font-serif text-lg text-primary">{profile?.institution_name}</p>
                <p className="text-xs text-ink-tertiary font-mono tracking-wider flex items-center gap-1.5">
                  <Lock className="h-3 w-3" /> LOCKED · main institution
                </p>
              </div>
              {!pendingChangeReq && (
                <button
                  type="button"
                  onClick={() => setShowChangeForm((s) => !s)}
                  className="text-xs font-mono tracking-wider text-primary hover:underline"
                >
                  {showChangeForm ? "Cancel" : "Request change"}
                </button>
              )}
            </div>

            {pendingChangeReq && (
              <div className="mt-3 border border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-sm">
                <p className="font-mono text-xs tracking-wider text-amber-700 dark:text-amber-400 mb-1">
                  PENDING REQUEST
                </p>
                <p className="text-ink-secondary">
                  You've asked to change to{" "}
                  <span className="font-serif text-primary">
                    {pendingChangeReq.requested_free_text ??
                      "another institution"}
                  </span>
                  . Support will review and update your profile.
                </p>
              </div>
            )}

            {showChangeForm && !pendingChangeReq && (
              <div className="mt-4 border border-rule bg-paper p-4 space-y-3">
                <div>
                  <Eyebrow as="label" className="block mb-2">New institution</Eyebrow>
                  <InstitutionPicker
                    value={requestedInst}
                    onChange={setRequestedInst}
                    emailDomainHint={emailDomain}
                  />
                </div>
                <div>
                  <label htmlFor="change-reason" className="block mb-2 text-eyebrow tracking-eyebrow uppercase font-semibold text-editorial-accent">
                    Reason for change
                  </label>
                  <textarea
                    id="change-reason"
                    rows={3}
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    maxLength={2000}
                    placeholder="e.g. transferred from X to Y in September, or graduated and started MSc at…"
                    className="w-full border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setShowChangeForm(false)}
                    className="px-3 py-2 text-sm border border-rule hover:bg-accent"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={
                      submitChangeReq.isPending ||
                      !requestedInst ||
                      changeReason.trim().length < 10
                    }
                    onClick={() => submitChangeReq.mutate()}
                    className="px-3 py-2 text-sm bg-primary text-primary-foreground disabled:opacity-50"
                  >
                    {submitChangeReq.isPending ? "Submitting…" : "Submit request"}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <InstitutionPicker
            value={currentValue}
            onChange={(v) => updateInst.mutate(v)}
            emailDomainHint={emailDomain}
            disabled={updateInst.isPending}
          />
        )}
      </section>

      {/* Prominent save & continue */}
      <section className="mb-10 border border-primary/30 bg-primary/5 p-6">
        <Eyebrow className="mb-2">3 · Next step</Eyebrow>
        <DisplayHeading level={2} className="text-2xl mb-2">
          {setupComplete ? "You're all set — start your first review" : "Almost there"}
        </DisplayHeading>
        <p className="text-sm text-ink-secondary mb-5">
          {setupComplete
            ? "Upload a draft and we'll critique it against your brief, marking criteria and references."
            : "Finish the steps above, then continue to upload your first draft for review."}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleSaveAndContinue}
            disabled={updatePrefName.isPending}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {nameDirty ? "Save & start first review" : "Start your first review"}
            <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 border border-rule bg-background px-5 py-3 text-sm hover:bg-accent"
          >
            <LayoutDashboard className="h-4 w-4" /> Go to dashboard
          </Link>
        </div>
      </section>

      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <div className="border border-rule bg-card p-6">
          <Eyebrow className="mb-1">Total reviews</Eyebrow>
          <p className="font-serif text-4xl text-primary">{total}</p>
        </div>
        <div className="border border-rule bg-card p-6">
          <Eyebrow className="mb-1">Completed</Eyebrow>
          <p className="font-serif text-4xl text-primary">{completed}</p>
        </div>
      </div>

      <FreeTermCard />
      <DirectoryListingCard />

      {/* Footer nav */}
      <nav className="flex flex-wrap justify-between gap-3 border-t border-rule pt-6 text-sm">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-ink-secondary hover:text-primary">
          <LayoutDashboard className="h-4 w-4" /> Dashboard
        </Link>
        <Link to="/reviews" className="inline-flex items-center gap-2 text-ink-secondary hover:text-primary">
          <FileText className="h-4 w-4" /> Your reviews
        </Link>
        <Link to="/reviews/new" className="inline-flex items-center gap-2 text-primary hover:underline">
          New review <ArrowRight className="h-4 w-4" />
        </Link>
      </nav>

      <p className="text-sm text-ink-tertiary mt-8">
        Post-marking reflection and signal aggregation will populate this profile.
      </p>
    </PageContainer>
  );
}
